
import React from "react";
import { useParams } from "react-router-dom";
import { usePublicProfile } from "@/hooks/use-public-profile";
import { LoadingFallback } from "@/components/common/LoadingFallback";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MiniProfileGrid } from "@/components/ui/mini-profile-grid";
import MiniProfileBox from "@/components/mini-profile/MiniProfileBox";
import { ExternalLink } from "lucide-react";
import BulletinTimeDisplay from "@/components/bulletins/BulletinTimeDisplay";
import { useAuth } from "@/contexts/AuthContext";

const MiniProfile = () => {
  const { username } = useParams();
  const { profile, isLoading, error } = usePublicProfile(username);
  const { user } = useAuth();
  
  const { data: creatorCount = 0 } = useQuery({
    queryKey: ['creator-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      return count || 0;
    }
  });

  const { data: recentBulletins = [] } = useQuery({
    queryKey: ['recent-bulletins', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      
      const { data, error } = await supabase
        .from('bulletins')
        .select('*')
        .eq('user_id', profile.id)
        .eq('is_mature', false)
        .order('created_at', { ascending: false })
        .limit(30); // Increased from 21 to 30 for more content to fill the grid
      
      if (error) {
        console.error("Error fetching bulletins:", error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!profile?.id
  });

  React.useEffect(() => {
    if (error) {
      console.error("Error loading profile:", error);
      toast.error("Error loading profile");
    }
  }, [error]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-400 text-lg">Profile not found</p>
        <p className="text-gray-500 text-sm mt-2">@{username}</p>
      </div>
    );
  }

  // Enhanced bulletin items creation with better content display
  const bulletinItems = recentBulletins.map((bulletin) => {
    // Determine background opacity based on content length for better readability
    const hasLongerContent = bulletin.content && bulletin.content.length > 50;
    const backgroundOpacity = hasLongerContent ? "opacity-30" : "opacity-50";
    
    return (
      <div
        key={bulletin.id}
        className="h-full w-full group"
      >
        <div className="h-full w-full p-4 flex flex-col items-center justify-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 group-hover:opacity-75 transition-opacity" />
          {bulletin.image_url && (
            <div 
              className={`absolute inset-0 ${backgroundOpacity}`}
              style={{
                backgroundImage: `url(${bulletin.image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          )}
          <div className="relative z-10 text-center p-3">
            <p className="text-sm line-clamp-3">{bulletin.content}</p>
            <div className="mt-2 flex justify-center">
              <BulletinTimeDisplay 
                createdAt={bulletin.created_at} 
                expiresAt={bulletin.expires_at}
                isUpdate={bulletin.is_update || false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  });

  // Ensure we're creating link items from the profile's custom_links
  const customLinks = Array.isArray(profile.custom_links) ? profile.custom_links : [];
  
  const linkItems = customLinks.map((link: any) => (
    <a
      key={link.url}
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="h-full w-full group"
    >
      <div className="h-full w-full p-4 flex flex-col items-center justify-center text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 group-hover:opacity-75 transition-opacity" />
        <div className="relative z-10 text-center">
          <h3 className="font-medium mb-2">{link.label}</h3>
          <ExternalLink className="w-5 h-5 mx-auto opacity-50 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </a>
  ));

  return (
    <div className="min-h-screen bg-black">
      <div className="relative">
        <MiniProfileGrid 
          profileContent={<MiniProfileBox key="profile" profile={profile} />}
          linkItems={linkItems}
          bulletinItems={bulletinItems}
          gradientColor="#0A0A0A"  // Even darker, pure off-black
          className="min-h-screen"
        />
        
        {/* Only show the join CTA if user is not logged in */}
        {!user && (
          <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
            <div className="container max-w-2xl mx-auto">
              <Link 
                to="/home" 
                className="block text-center bg-background/80 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-background/90 hover:border-white/20 transition-all"
              >
                <span className="text-white/90">
                  Join {creatorCount.toLocaleString()} other creators on FOMO!
                </span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniProfile;
